import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { EMPTY_ARRAY } from '@modules/common/constants';
import { Category } from '@modules/common/types';
import { QuantifyFeedbackListDrawer } from '@modules/quantify/components/QuantifyFeedbackListDrawer';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import { DEFAULT_QUANTIFY_SORT_BY } from '@modules/quantify/constants';
import {
  useGetQuantifyCompareQuery,
  useGetQuantifyDuration,
  useGetQuantifyReportTableColumns,
  useGetQuantifyWhereClause,
} from '@modules/quantify/hooks';
import { useFetchQuantifyChartData } from '@modules/quantify/hooks/useFetchQuantifyChartData';
import { useFetchQuantifyTableData } from '@modules/quantify/hooks/useFetchQuantifyTableData';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareQueryContainer,
  getQuantifyQueryContainer,
  getVisibleDataPointsLimit,
  trackQuantifyCompareEvents,
} from '@modules/quantify/utils';
import { getMetricKeyID } from '@modules/quantify/utils/getMetricKeyID';
import { Box, Checkbox, Paper } from '@mui/material';
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridPinnedColumns,
  GridRowScrollEndParams,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { QuantifyVisualisationType } from 'generated/graphql';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToggle } from 'react-use';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { TimeRange } from 'v2/core/entities';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';
import { shallow } from 'zustand/shallow';
import { QuantifyReportTableHeader } from './QuantifyReportTableHeader';
import { QuantifyReportTableNoResultsOverlay } from './QuantifyReportTableNoResultsOverlay';
import { useGridInitialState } from './hooks';

const trackQuantifyReportViewFeedbackEvents = createAnalyticsTracker(
  'quantifyReportViewFeedback',
);

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');

interface QuantifyReportTableParams {
  shouldHideToolbar?: boolean;
  shouldBlockSelection?: boolean;
  shouldHideActions?: boolean;
  isCountTable?: boolean;
  pinnedColumns?: GridPinnedColumns;
}

export function QuantifyReportTable({
  shouldHideToolbar = false,
  shouldBlockSelection = false,
  shouldHideActions = false,
  isCountTable,
  pinnedColumns,
}: QuantifyReportTableParams) {
  const abortControllerRef = useRef(new AbortController());
  const { showNotification } = useNotificationContext();

  const [fetchQuantifyTableData] = useFetchQuantifyTableData({
    shouldFetchCount: isCountTable,
  });
  const [fetchQuantifyChartData] = useFetchQuantifyChartData({
    abortSignal: abortControllerRef.current.signal,
  });
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const query = useQuantifyStore(({ query }) => query);
  const compareQuery = useGetQuantifyCompareQuery();
  const reportTableRef = useGridApiRef();
  const {
    sortBy,
    visibleDataPoints,
    activeVisualisationType,
    shouldShowTopDataPoints,
    pageNumber,
    isTableQueryInProgress,
    tableAggregateHits,
    shouldShowAllValues,
    topDataPointsSelectionCount,
  } = useQuantifyStore(({ visual, query, reportData }) => {
    return {
      sortBy: query.sortBy ?? DEFAULT_QUANTIFY_SORT_BY,
      visibleDataPoints:
        query.visibleDataPoints?.map((id) => JSON.stringify(id)) ?? [],
      activeVisualisationType: query.visualisationType,
      shouldShowTopDataPoints: query.shouldShowTopDataPoints,
      pageNumber: visual.pageNumber,
      isTableQueryInProgress: visual.isTableQueryInProgress,
      tableAggregateHits: visual.tableAggregateHits,
      shouldShowAllValues: visual.shouldShowAllValues,
      topDataPointsSelectionCount: query.topDataPointsSelectionCount,
    };
  }, shallow);

  const tableRows = useQuantifyStore(
    ({ reportData }) =>
      (compareQuery
        ? reportData.compare.tableRows
        : reportData.quantify.tableRows) ?? EMPTY_ARRAY,
  );

  // TODO: MUI-X DataGrid Pro throws error when sortByField is not present in columns, which can be true in case of anomalies.
  // Remove this check once the issue is fixed. https://github.com/mui/mui-x/issues/11483
  const gridInitialState = useGridInitialState(
    !tableRows.length ? DEFAULT_QUANTIFY_SORT_BY : sortBy,
  );

  const [shouldOpenFeedbackDrawer, toggleShouldOpenFeedbackDrawer] =
    useToggle(false);

  const whereClause = useGetQuantifyWhereClause();
  const duration = useGetQuantifyDuration();

  const [selectedMetricKeyId, setSelectedMetricKeyId] = useState('');
  const [selectedMetricKeyLabel, setSelectedMetricKeyLabel] = useState('');
  const [selectedMetricKeyCategory, setSelectedMetricKeyCategory] =
    useState<Category>();
  const [isSelectedCompareFieldA, setIsSelectedCompareFieldA] = useState(false);
  const [isSelectedMetricKeyInvisible, setIsSelectedMetricKeyInvisible] =
    useState(false);
  const [
    isSelectedMetricKeyUpdateInProgress,
    setIsSelectedMetricKeyUpdateInProgress,
  ] = useState(false);

  const [feedbackQueryContainer, setFeedbackQueryContainer] =
    useState<QueryContainer | null>(null);
  const [shouldFetchChartData, setShouldFetchChartData] = useState(false);
  const [shouldFetchTableData, setShouldFetchTableData] = useState(false);

  const analysisLink = useAnalysisStore(({ href }) => href);

  const showFeedbackDrawer = useCallback(
    (params: ShowFeedbackDrawerFromTableOptions) => {
      toggleShouldOpenFeedbackDrawer(true);
      setSelectedMetricKeyId(params.id);
      setSelectedMetricKeyLabel(params.label);
      setSelectedMetricKeyCategory(params.category);
      setIsSelectedCompareFieldA(params.isCompareFieldA ?? false);
      setIsSelectedMetricKeyInvisible(params.isInvisible);
      setIsSelectedMetricKeyUpdateInProgress(params.isUpdateInProgress);

      const queryContainer = compareQuery
        ? getCompareQueryContainer({
            query,
            isFieldA: params.isCompareFieldA ?? false,
            metricKeyId:
              getMetricKeyID({
                id: params.id,
                field: query.groupByField?.field,
              }) ?? '',
            metricCategory: params.category,
            whereClause,
            compareQuery,
            duration,
          })
        : getQuantifyQueryContainer({
            query,
            metricKeyId:
              getMetricKeyID({
                id: params.id,
                field: query.groupByField?.field,
              }) ?? '',
            metricCategory: params.category,
            dateRange: params.dateRange || (duration as TimeRange),
            whereClause:
              params.whereClause && whereClause
                ? combineWhereClauses(
                    whereClause,
                    params.whereClause,
                    OperationType.AND,
                  )
                : whereClause || params.whereClause,
          });

      setFeedbackQueryContainer(queryContainer);

      if (compareQuery) {
        trackQuantifyCompareEvents({
          eventName: 'Quantify Compare - Opened Feedback Drawer',
          link: analysisLink,
          from: 'table',
        });
      } else {
        trackQuantifyReportViewFeedbackEvents({
          eventName: 'Quantify Report - View Feedback',
          trigger: 'Table',
          metricKeyId: params.id,
          metricKeyLabel: params.label,
          query: {
            showMeLabel: query.groupByField?.displayName,
          },
        });
      }
    },
    [
      analysisLink,
      compareQuery,
      duration,
      query,
      toggleShouldOpenFeedbackDrawer,
      whereClause,
    ],
  );

  const { name, href, scope } = useAnalysisStore(
    (analysis) => ({
      name: analysis.title,
      href: analysis.href,
      scope: analysis.scope,
    }),
    shallow,
  );

  const onSelect = (selectedRows: GridRowSelectionModel) => {
    const selectionLimit = getVisibleDataPointsLimit(activeVisualisationType);

    if (selectedRows.length > selectionLimit) {
      // show notification
      showNotification({
        contentComponent: 'Maximum values selected.',
        props: { severity: 'warning', zIndex: 10000 },
      });
    }

    const slicedSelectedRows = selectedRows
      .slice(0, selectionLimit)
      .map((id) => JSON.parse(String(id)));

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        visibleDataPoints: slicedSelectedRows as string[],
      },
    });

    // Abort any possible previous API calls
    abortControllerRef.current.abort();
    // Reset the signal
    abortControllerRef.current = new AbortController();

    setShouldFetchChartData(true);

    trackQuantifyReportEvents({
      eventName: 'Quantify Report - Table Item Selection Changed',
      optionLabelIDs: slicedSelectedRows,
      name,
      link: href,
      scope: mapFromEdgeScope(scope),
    });
  };

  const onCheckboxClick = useCallback(() => {
    if (shouldShowTopDataPoints) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          shouldShowTopDataPoints: false,
        },
      });
      trackQuantifyReportEvents({
        eventName: 'Quantify Report - Dismissed Top X To Select Custom Values',
        name,
        link: href,
      });
    }
  }, [dispatch, href, name, shouldShowTopDataPoints]);

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      const gridSortItem = sortModel[0];

      if (gridSortItem) {
        const sortBy = {
          field: gridSortItem.field,
          isAsc: gridSortItem.sort === 'asc',
        };

        dispatch({
          type: QuantifyActionType.SET_QUERY,
          payload: { sortBy },
        });

        dispatch({
          type: QuantifyActionType.SET_VISUAL,
          payload: {
            pageNumber: 1,
          },
        });

        setShouldFetchTableData(true);
        setShouldFetchChartData(true);

        trackQuantifyReportEvents({
          eventName: 'Quantify Report - Sort By Updated',
          sortBy,
          name,
          link: href,
          scope: mapFromEdgeScope(scope),
        });
      }
    },
    [dispatch, href, name, scope],
  );

  const selectionLimit = getVisibleDataPointsLimit(activeVisualisationType);

  const selectionModel = useMemo(() => {
    if (topDataPointsSelectionCount && shouldShowTopDataPoints) {
      return tableRows
        .slice(0, topDataPointsSelectionCount)
        .map(({ id }) => JSON.stringify(id));
    }
    return visibleDataPoints?.length > selectionLimit
      ? visibleDataPoints?.slice(0, selectionLimit)
      : visibleDataPoints;
  }, [
    selectionLimit,
    visibleDataPoints,
    topDataPointsSelectionCount,
    tableRows,
    shouldShowTopDataPoints,
  ]);

  const { columns, columnGroupingModel } = useGetQuantifyReportTableColumns({
    onFeedbackCountClick: showFeedbackDrawer,
    shouldShowRadioColumn: selectionLimit === 1,
    selectionModel,
    shouldHideActions,
    formulaID: isCountTable
      ? EQLFunctionName.Count
      : query.formulaQuery?.eqlFunction,
  });

  const shouldPinLabelColumn =
    columns.length > 6 ||
    activeVisualisationType === QuantifyVisualisationType.Trend;

  const renderQuantifyReportTableHeader = useCallback(
    () => <QuantifyReportTableHeader />,
    [],
  );

  useEffect(() => {
    if (!reportTableRef?.current || !shouldPinLabelColumn) {
      return;
    }

    const sortedColumnIndex = reportTableRef.current.getColumnIndex(
      sortBy.field.toString(),
      false,
    );

    if (sortedColumnIndex === -1) {
      return;
    }

    // https://github.com/mui/mui-x/issues/6411
    const timer = setTimeout(() => {
      reportTableRef.current.scrollToIndexes({
        colIndex: sortedColumnIndex,
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [reportTableRef, sortBy.field, columns, shouldPinLabelColumn]);

  const onRowsScrollEnd = (params: GridRowScrollEndParams) => {
    // If the selected option is chosen, refer to the length of the tableRows array, taking into account the possibility that the API may return fewer records than requested.
    const limit = shouldShowAllValues ? tableAggregateHits : tableRows.length;

    if (tableRows.length >= limit || params.viewportPageSize === 0) {
      return;
    }

    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: { pageNumber: pageNumber + 1 },
    });

    setShouldFetchTableData(true);
  };

  useEffect(() => {
    if (shouldFetchTableData) {
      fetchQuantifyTableData();
      setShouldFetchTableData(false);
    }
  }, [shouldFetchTableData, fetchQuantifyTableData]);

  useEffect(() => {
    if (shouldFetchChartData) {
      if (!isCountTable) {
        fetchQuantifyChartData();
      }

      setShouldFetchChartData(false);
    }
  }, [shouldFetchChartData, fetchQuantifyChartData, isCountTable]);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        padding: 1.25,
        width: '100%',
      }}
    >
      <Box
        sx={{ height: 520, width: '100%' }}
        data-role="quantify-table-wrapper"
      >
        <DataGridPro
          apiRef={reportTableRef}
          paginationMode="server"
          sortingMode="server"
          loading={isTableQueryInProgress}
          rowCount={tableAggregateHits}
          onRowsScrollEnd={onRowsScrollEnd}
          experimentalFeatures={{ columnGrouping: true }}
          rows={tableRows}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          initialState={gridInitialState}
          rowHeight={42}
          checkboxSelection={shouldBlockSelection ? false : selectionLimit > 1}
          disableRowSelectionOnClick={
            shouldBlockSelection ? true : selectionLimit > 1
          }
          pinnedColumns={
            pinnedColumns ?? {
              left: [
                GRID_CHECKBOX_SELECTION_COL_DEF.field,
                shouldPinLabelColumn ? 'label' : '',
              ],
            }
          }
          hideFooter
          disableColumnMenu
          keepNonExistentRowsSelected
          onRowSelectionModelChange={onSelect}
          onSortModelChange={onSortModelChange}
          sortModel={gridInitialState.sorting?.sortModel}
          rowSelectionModel={selectionModel}
          sx={{
            border: 'none',
          }}
          getRowId={({ id }) =>
            /** Metadata are values that can be anything.
             *  they can be in different language,
             * can contain special characters etc which MUI might not be able to comprehend.
             * This ensures we always pass string and parse ids back before consuming. See onSelect function */
            JSON.stringify(id)
          }
          slots={{
            toolbar: shouldHideToolbar ? null : renderQuantifyReportTableHeader,
            noResultsOverlay: QuantifyReportTableNoResultsOverlay,
            baseCheckbox: (props) => (
              <Checkbox {...props} onClick={onCheckboxClick} />
            ),
          }}
          slotProps={{
            loadingOverlay: {
              'aria-label': 'quantify-report-table-loading-overlay',
            },
          }}
          disableMultipleColumnsSorting
        />
        {feedbackQueryContainer && (
          <QuantifyFeedbackListDrawer
            open={shouldOpenFeedbackDrawer}
            onClose={toggleShouldOpenFeedbackDrawer}
            queryContainer={feedbackQueryContainer}
            metricKeyLabel={selectedMetricKeyLabel}
            metricKeyId={selectedMetricKeyId}
            metricKeyCategory={selectedMetricKeyCategory}
            isCompareFieldA={isSelectedCompareFieldA}
            isMetricKeyInvisible={isSelectedMetricKeyInvisible}
            isMetricKeyUpdateInProgress={isSelectedMetricKeyUpdateInProgress}
          />
        )}
      </Box>
    </Paper>
  );
}
